import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ClipLoader } from '../../atoms/clip-loader';
import { DynamicSVG } from '../../atoms/dynamic-svg';
import copyIcon from '../../assets/icons/document-copy.svg';
import downloadIconV2 from '../../assets/icons/document-download-v2.svg';
import editIconV2 from '../../assets/icons/document-edit-v2.svg';
import deleteIconV2 from '../../assets/icons/document-delete-v2.svg';
import signContractIcon from '../../assets/icons/sign-contract.svg';
import { DropdownMenu, DropdownMenuItem, MenuIconWrap } from './styles';

const menuIconStyle = { width: 20, height: 20, color: '#797e86' };
const MENU_OFFSET = 4;
const MENU_MIN_WIDTH = 180;

const MenuIcon = ({ svgPath }) => (
  <MenuIconWrap>
    <DynamicSVG svgPath={svgPath} style={menuIconStyle} />
  </MenuIconWrap>
);

const DocumentActionsDropdown = ({
  buttonRef,
  menuRef,
  intl,
  onCopySigningLink,
  onSignContract,
  onDownload,
  onEdit,
  onDelete,
  isDeleting = false,
  isControlPanel = true,
  contractStatus,
  contractType,
  creatorContract = false,
  shouldDisableEditAndDelete = false,
  isDownloading = false,
  showCopySigningLink
}) => {
  const [menuCoords, setMenuCoords] = useState({ top: 0, left: 0 });
  const menuRefInternal = useRef(null);

  const showCopyLink =
    showCopySigningLink ??
    (contractType !== null && contractStatus === 'to_sign' && isControlPanel);

  useEffect(() => {
    if (!buttonRef) return undefined;

    const updatePosition = () => {
      if (!buttonRef) return;

      const buttonRect = buttonRef.getBoundingClientRect();
      const menuEl = menuRefInternal.current;
      const menuHeight = menuEl?.offsetHeight || 180;
      const menuWidth = menuEl?.offsetWidth || MENU_MIN_WIDTH;
      const spaceBelow = window.innerHeight - buttonRect.bottom;
      const spaceAbove = buttonRect.top;
      const openUpward = spaceBelow < menuHeight && spaceAbove >= menuHeight;

      const top = openUpward
        ? buttonRect.top - menuHeight - MENU_OFFSET
        : buttonRect.bottom + MENU_OFFSET;
      const left = Math.max(8, buttonRect.right - menuWidth);

      setMenuCoords({ top, left });
    };

    const timeoutId = setTimeout(updatePosition, 0);
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [buttonRef]);

  useEffect(() => {
    if (!buttonRef || !menuRefInternal.current) return undefined;

    const observer = new ResizeObserver(() => {
      const buttonRect = buttonRef.getBoundingClientRect();
      const menuEl = menuRefInternal.current;
      if (!menuEl) return;

      const menuHeight = menuEl.offsetHeight;
      const menuWidth = menuEl.offsetWidth;
      const spaceBelow = window.innerHeight - buttonRect.bottom;
      const spaceAbove = buttonRect.top;
      const openUpward = spaceBelow < menuHeight && spaceAbove >= menuHeight;

      setMenuCoords({
        top: openUpward
          ? buttonRect.top - menuHeight - MENU_OFFSET
          : buttonRect.bottom + MENU_OFFSET,
        left: Math.max(8, buttonRect.right - menuWidth)
      });
    });

    observer.observe(menuRefInternal.current);
    return () => observer.disconnect();
  }, [buttonRef]);

  const combinedRef = (el) => {
    menuRefInternal.current = el;
    if (menuRef) menuRef(el);
  };

  const runAction = (handler) => (e) => {
    e.stopPropagation();
    handler?.();
  };

  if (typeof document === 'undefined') return null;

  return createPortal(
    <DropdownMenu
      ref={combinedRef}
      $top={menuCoords.top}
      $left={menuCoords.left}
      onClick={(e) => e.stopPropagation()}
    >
      { contractStatus !== 'sign' && showCopyLink && onCopySigningLink && (
        <DropdownMenuItem onClick={runAction(onCopySigningLink)}>
          <MenuIcon svgPath={copyIcon} />
          <span>
            {intl?.formatMessage?.({ id: 'campaign.documents.copySigningLink' }) ||
              'Copy signing link'}
          </span>
        </DropdownMenuItem>
      )}
      {contractType !== null &&
        contractStatus === 'to_sign' &&
        !isControlPanel &&
        !creatorContract &&
        onSignContract && (
        <DropdownMenuItem onClick={runAction(onSignContract)}>
          <MenuIcon svgPath={signContractIcon} />
          <span>
            {intl?.formatMessage?.({ id: 'campaign.documents.signContract' }) || 'Sign contract'}
          </span>
        </DropdownMenuItem>
      )}
      {onDownload && (
        <DropdownMenuItem
          $disabled={isDownloading}
          onClick={runAction(isDownloading ? undefined : onDownload)}
        >
          {isDownloading ? (
            <ClipLoader size={14} loading color="#797e86" />
          ) : (
            <MenuIcon svgPath={downloadIconV2} />
          )}
          <span>{intl?.formatMessage?.({ id: 'campaign.documents.download' }) || 'Download'}</span>
        </DropdownMenuItem>
      )}
      {onEdit && (
        <DropdownMenuItem
          $disabled={shouldDisableEditAndDelete}
          title={shouldDisableEditAndDelete ? 'Not available' : undefined}
          onClick={runAction(shouldDisableEditAndDelete ? undefined : onEdit)}
        >
          <MenuIcon svgPath={editIconV2} />
          <span>
            {intl?.formatMessage?.({ id: 'campaign.documents.editTitle' }) || 'Edit title'}
          </span>
        </DropdownMenuItem>
      )}
      {onDelete && (
        <DropdownMenuItem
          $disabled={isDeleting || shouldDisableEditAndDelete}
          title={shouldDisableEditAndDelete ? 'Not available' : undefined}
          onClick={runAction(
            isDeleting || shouldDisableEditAndDelete ? undefined : onDelete
          )}
        >
          <MenuIcon svgPath={deleteIconV2} />
          <span>{intl?.formatMessage?.({ id: 'campaign.documents.delete' }) || 'Delete'}</span>
        </DropdownMenuItem>
      )}
    </DropdownMenu>,
    document.body
  );
};

export { DocumentActionsDropdown };
